import type { UserRole } from '@oefy/contracts';
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { Lock, LogOut, RotateCcw, WifiOff } from 'lucide-react';
import { Button, ButtonLink } from '../ui/button';
import { LoadingScreen } from '../ui/spinner';
import { StatusScreen } from '../ui/status-screen';
import { useSession, useSignOut } from './session';

interface RequireSessionProps {
  readonly role?: UserRole;
  readonly children: ReactNode;
}

export function RequireSession({ role, children }: RequireSessionProps) {
  const session = useSession();
  const location = useLocation();
  const signOut = useSignOut();

  if (session.isPending) {
    return <LoadingScreen />;
  }

  if (session.isError) {
    return (
      <StatusScreen
        tone="warning"
        pose="sleep"
        icon={<WifiOff aria-hidden="true" />}
        title="Oefy is even niet bereikbaar"
        actions={
          <Button onClick={() => void session.refetch()}>
            <RotateCcw aria-hidden="true" />
            Opnieuw proberen
          </Button>
        }
      >
        Controleer je internetverbinding en probeer het opnieuw.
      </StatusScreen>
    );
  }

  if (!session.data) {
    const back = encodeURIComponent(`${location.pathname}${location.search}`);
    return <Navigate to={`/inloggen?terug=${back}`} replace />;
  }

  if (role && session.data.role !== role) {
    return (
      <StatusScreen
        tone="neutral"
        pose="idle"
        icon={<Lock aria-hidden="true" />}
        title="Geen toegang"
        actions={
          <>
            <Button
              variant="secondary"
              busy={signOut.isPending}
              onClick={() => {
                signOut.mutate();
              }}
            >
              <LogOut aria-hidden="true" />
              Uitloggen
            </Button>
            <ButtonLink to="/">Naar de startpagina</ButtonLink>
          </>
        }
      >
        Je bent ingelogd als{' '}
        <span className="font-medium text-slate-900">{session.data.email}</span>. Dit deel van Oefy
        is niet voor dit account.
      </StatusScreen>
    );
  }

  return children;
}
